import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
  _Tooltip,
} from '@mui/material';
import { Lock, Tag, Refresh as RefreshIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import {
  getSlackChannels,
  refreshSlackChannels,
  getSlackChannelSyncInfo,
} from 'client/modules/Integrations/IntegrationsActions';
import useDebounce from 'client/util/useDebounce';
import messages from './messagesSlack';
import moment from 'moment';

function SlackRuleModal({ open, onClose, ruleIdx, onSave, rules, surveys, cartesian, icons }) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const editing = typeof ruleIdx === 'number';
  const ruleData = editing ? rules[ruleIdx] : {};
  const [form, setForm] = useState({
    channel: ruleData?.channel || '',
    condition: ruleData?.condition || 'group:all,commented:true',
    surveys: ruleData?.surveys || [],
    disabled: ruleData?.disabled || false,
  });
  const [errors, setErrors] = useState({});
  const [channels, setChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [channelsAvailable, setChannelsAvailable] = useState(true);
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [syncInfo, setSyncInfo] = useState(null);
  const debouncedInputValue = useDebounce(inputValue, 300);

  const fetchChannels = useCallback(
    async (searchQuery = '') => {
      setChannelsLoading(true);
      try {
        const channelsData = await dispatch(getSlackChannels(null, searchQuery));
        setChannels(channelsData);

        if (channelsData.length === 0 && !searchQuery.trim()) {
          setChannelsAvailable(false);
        } else {
          setChannelsAvailable(true);
        }
      } catch (_error) {
        setChannels([]);
        setChannelsAvailable(false);
      } finally {
        setChannelsLoading(false);
      }
    },
    [dispatch],
  );

  const fetchSyncInfo = useCallback(async () => {
    try {
      const syncData = await dispatch(getSlackChannelSyncInfo(null));
      setSyncInfo(syncData);
    } catch (_error) {
      setSyncInfo({ error: true });
    }
  }, [dispatch]);

  // Poll for sync completion when sync is in progress and modal is open
  useEffect(() => {
    if (syncInfo && syncInfo.syncing && open) {
      const interval = setInterval(() => {
        fetchSyncInfo();
      }, 5000); // Check every 5 seconds

      return () => clearInterval(interval);
    }
  }, [syncInfo, fetchSyncInfo, open]);

  const handleRefreshChannels = useCallback(async () => {
    if (refreshing) return; // Prevent double refresh

    setRefreshing(true);
    try {
      const result = await dispatch(refreshSlackChannels(null));

      if (result.syncing) {
        // Sync started in background, just fetch sync info to start polling
        await fetchSyncInfo();
      } else {
        // Sync completed immediately (unlikely with large channel counts)
        await fetchChannels();
        await fetchSyncInfo();
      }
    } catch (_error) {
      // Error handled by sync info state
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, refreshing, fetchChannels, fetchSyncInfo]);

  // Trigger search when debounced input value changes
  useEffect(() => {
    if (debouncedInputValue !== inputValue) {
      return; // Don't search if we're still typing
    }
    fetchChannels(debouncedInputValue);
  }, [debouncedInputValue, fetchChannels, inputValue]);

  useEffect(() => {
    if (editing) {
      setForm({
        channel: ruleData?.channel || '',
        condition: ruleData?.condition || 'group:all,commented:true',
        surveys: ruleData?.surveys || [],
        disabled: ruleData?.disabled || false,
      });
    } else {
      setForm({
        channel: '',
        condition: 'group:all,commented:true',
        surveys: [],
        disabled: false,
      });
    }
    setErrors({});

    // Fetch channels and sync info when modal opens
    if (open) {
      fetchChannels(); // Fetch initial channels
      fetchSyncInfo(); // Fetch sync info
    }
  }, [
    editing,
    fetchChannels,
    fetchSyncInfo,
    open,
    ruleData?.channel,
    ruleData?.condition,
    ruleData?.disabled,
    ruleData?.surveys,
    ruleIdx,
  ]);

  const handleChange = useCallback(
    field => event => {
      setForm(f => ({ ...f, [field]: event.target.value }));
    },
    [],
  );

  const handleInputChange = useCallback((_, value) => {
    setInputValue(value);
  }, []);

  const handleCheckboxChange = useCallback(
    field => event => {
      setForm(f => ({ ...f, [field]: event.target.checked }));
    },
    [],
  );

  const handleChannelChange = useCallback((event, value) => {
    setForm(f => ({ ...f, channel: value ? value.name : '' }));
    setValue(value);
  }, []);

  const getOptionLabel = useCallback(option => {
    return option.display_name || option.name || '';
  }, []);

  const _getChannelValue = useCallback(() => {
    return channels.find(channel => channel.name === form.channel) || null;
  }, [channels, form.channel]);

  const renderInput = useCallback(
    params => (
      <TextField
        {...params}
        required
        error={!!errors.channel}
        helperText={errors.channel}
        variant="outlined"
        label={formatMessage(messages.slackChannel)}
        autoFocus
        placeholder={
          channelsLoading
            ? formatMessage(messages.loadingChannels)
            : formatMessage(messages.typeToSearch)
        }
        fullWidth
      />
    ),
    [channelsLoading, errors.channel, formatMessage],
  );

  const renderOption = useCallback(
    (props, option) => (
      <li {...props}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            overflow: 'hidden',
          }}
        >
          {/* Channel icon */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 20,
              height: 20,
              borderRadius: '3px',
              backgroundColor: 'background.light',
              color: 'text.secondary',
              fontSize: '0.875rem',
            }}
          >
            {option.is_private ? (
              <Lock sx={{ fontSize: '0.875rem' }} />
            ) : (
              <Tag sx={{ fontSize: '0.875rem' }} />
            )}
          </Box>

          {/* Channel info */}
          <Box
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={option.name}
          >
            {option.name}
          </Box>
        </Box>
      </li>
    ),
    [],
  );

  // Custom handler for surveys to support 'All' logic
  const handleSurveysChange = useCallback(event => {
    const { value } = event.target;
    if (value.includes('all')) {
      setForm(f => ({ ...f, surveys: [] })); // 'All' means all surveys (empty array)
    } else {
      setForm(f => ({ ...f, surveys: value }));
    }
  }, []);

  const handleSave = () => {
    const errs = {};
    if (!form.channel) {
      errs.channel = formatMessage(messages.channelRequired);
    } else if (!channelsAvailable) {
      // For manual entry, validate channel name format
      const cleanChannel = form.channel.replace(/^#/, '');
      if (!cleanChannel.match(/^[a-zA-Z0-9_-]{1,80}$/)) {
        errs.channel = formatMessage(messages.channelFormat);
      }
    }
    // Note: Empty surveys array is valid as it represents "All surveys"
    // Only show error if surveys is null/undefined, not if it's an empty array
    if (!form.surveys) {
      errs.surveys = formatMessage(messages.surveysRequired);
    }
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    onSave({ ...form }, editing ? ruleIdx : undefined);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editing ? formatMessage(messages.edit) : formatMessage(messages.addRule)}
      </DialogTitle>
      <DialogContent sx={{ pb: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <FormControl
          variant="outlined"
          sx={{
            minWidth: 220,
            mb: 2,
            display: 'flex',
            flexDirection: 'row',
            gap: 3,
            alignItems: 'baseline',
          }}
          fullWidth
        >
          {channelsAvailable ? (
            <>
              <Autocomplete
                sx={{
                  minWidth: 500,
                  '& .MuiAutocomplete-paper': {
                    maxHeight: 300,
                  },
                  '& .MuiAutocomplete-listbox': {
                    padding: 0,
                  },
                  '& .MuiAutocomplete-option': {
                    padding: 0,
                    margin: 0,
                    borderRadius: 1,
                    '&.Mui-focused': {
                      backgroundColor: 'action.selected',
                    },
                  },
                }}
                variant="outlined"
                key={`slack-autocomplete-${open}-${ruleIdx}`}
                options={channels}
                getOptionLabel={getOptionLabel}
                value={value}
                onChange={handleChannelChange}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                loading={channelsLoading}
                freeSolo={false}
                renderInput={renderInput}
                renderOption={renderOption}
                noOptionsText={
                  channelsLoading
                    ? formatMessage(messages.loading)
                    : formatMessage(messages.noChannelsFound)
                }
                autoFocus
              />
              {/* Refresh button and sync info - always show */}
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <span>
                  {refreshing || (syncInfo && syncInfo.syncing) ? (
                    <Box sx={{ mr: 1, height: 20 }}>
                      <CircularProgress
                        size={20}
                        sx={{ color: 'text.secondary' }}
                        enableTrackSlot
                        thickness={5}
                      />
                    </Box>
                  ) : (
                    <IconButton
                      size="small"
                      onClick={handleRefreshChannels}
                      variant="outlined"
                      title={formatMessage(messages.refreshChannels)}
                    >
                      <RefreshIcon />
                    </IconButton>
                  )}
                </span>

                {/* Show last update info when not syncing and no errors */}
                {syncInfo &&
                  !syncInfo.syncing &&
                  syncInfo.lastSynced &&
                  !syncInfo.missingScope &&
                  !syncInfo.error && (
                    <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {formatMessage(messages.lastUpdate, {
                        date: moment(syncInfo.lastSynced).format('lll'),
                      })}
                    </Box>
                  )}

                {syncInfo && (refreshing || (syncInfo && syncInfo.syncing)) && (
                  <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {refreshing
                      ? formatMessage(messages.startingSync)
                      : syncInfo && syncInfo.syncing
                        ? syncInfo.syncProgress > 0
                          ? `${formatMessage(messages.syncingChannels)} ${formatMessage(
                              messages.requestCompleted,
                              {
                                count: syncInfo.channelCount,
                              },
                            )}`
                          : `${formatMessage(messages.syncingChannels)} ${formatMessage(
                              messages.thisMayTakeTime,
                            )}`
                        : formatMessage(messages.refreshChannels)}
                  </Box>
                )}

                {/* Show error/warning messages only */}
                {syncInfo && !syncInfo.syncing && syncInfo.missingScope && (
                  <Box sx={{ fontSize: '0.75rem', color: 'warning.main' }}>
                    {formatMessage(messages.missingPermissions)}
                  </Box>
                )}

                {syncInfo && !syncInfo.syncing && syncInfo.error && (
                  <Box sx={{ fontSize: '0.75rem', color: 'error.main' }}>
                    {formatMessage(messages.syncFailed)}
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <>
              <TextField
                required
                value={form.channel}
                onChange={handleChange('channel')}
                error={!!errors.channel}
                helperText={errors.channel || formatMessage(messages.channelNameHelper)}
                variant="outlined"
                label={formatMessage(messages.slackChannel)}
                placeholder={formatMessage(messages.channelPlaceholder)}
                fullWidth
              />
              <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
                {formatMessage(messages.channelListUnavailable)}
              </Box>
            </>
          )}
        </FormControl>
        <FormControl variant="outlined" sx={{ minWidth: 220, mb: 2 }} fullWidth>
          <InputLabel>{formatMessage(messages.condition)}</InputLabel>
          <Select
            value={form.condition}
            onChange={handleChange('condition')}
            label={formatMessage(messages.condition)}
            required
            renderValue={selected => {
              const [group, commented] = selected.replace('group:', '').split(',commented:');
              const groupLabel = group.charAt(0).toUpperCase() + group.slice(1);
              const icon = icons[groupLabel] || null;
              const label = formatMessage(
                messages[`condition${commented === 'true' ? 'Commented' : 'All'}${groupLabel}`],
              );
              return (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {icon}
                  {label}
                </span>
              );
            }}
          >
            {cartesian(['All', 'Promoters', 'Passives', 'Detractors'], [false, true]).map(
              ([group, commented]) => (
                <MenuItem
                  value={`group:${group.toLowerCase()},commented:${commented}`}
                  key={group + commented}
                >
                  <ListItemIcon>{icons[group]}</ListItemIcon>
                  {formatMessage(messages[`condition${commented ? 'Commented' : 'All'}${group}`])}
                </MenuItem>
              ),
            )}
          </Select>
        </FormControl>
        <FormControl variant="outlined" sx={{ minWidth: 220, mb: 2 }} fullWidth>
          <InputLabel>{formatMessage(messages.surveys)}</InputLabel>
          <Select
            multiple
            value={form.surveys}
            onChange={handleSurveysChange}
            label={formatMessage(messages.surveys)}
            renderValue={values =>
              values.length === 0
                ? formatMessage(messages.all)
                : values
                    .map(v => (surveys.find(s => s.id === v) || {}).name)
                    .filter(v => v)
                    .join(', ')
            }
            fullWidth
            required
          >
            <MenuItem value="all">
              <Checkbox checked={form.surveys.length === 0} />
              {formatMessage(messages.all)}
            </MenuItem>
            {surveys.map(s => (
              <MenuItem key={s.id} value={s.id}>
                <Checkbox checked={form.surveys.length === 0 || form.surveys.includes(s.id)} />
                <ListItemText primary={s.name} />
              </MenuItem>
            ))}
          </Select>
          {errors.surveys && <div style={{ color: 'red', fontSize: 12 }}>{errors.surveys}</div>}
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={!form.disabled}
              onChange={e =>
                handleCheckboxChange('disabled')({ target: { checked: !e.target.checked } })
              }
              color="primary"
            />
          }
          label={formatMessage(messages.enabled)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{formatMessage(messages.cancel) || 'Cancel'}</Button>
        <Button onClick={handleSave} variant="contained">
          {formatMessage(messages.save) || 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SlackRuleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  ruleIdx: PropTypes.number,
  onSave: PropTypes.func.isRequired,
  rules: PropTypes.array.isRequired,
  surveys: PropTypes.array.isRequired,
  messages: PropTypes.object.isRequired,
  cartesian: PropTypes.func.isRequired,
  icons: PropTypes.object.isRequired,
};

export default SlackRuleModal;
